import type { FindingKind } from "./ocenaTypy.js";

/**
 * Kolor wypowiedzi w transkrypcie.
 *
 * Wynika WYLACZNIE ze zweryfikowanych wskazan przy tej wypowiedzi. Brak wskazania
 * nie jest ocena "srednio" - jest brakiem oceny i wypowiedz zostaje bez koloru
 * (w legendzie: "bez wskazan"). Wskazanie, ktorego cytatu nie udalo sie znalezc w
 * tekscie wypowiedzi (quoteVerified === false), nie barwi niczego: kolor jest
 * dowodem, a dowodu, ktorego nie ma, nie pokazujemy.
 *
 *   przy wypowiedzi sa                         kolor
 *   ------------------------------------------ ---------
 *   wylacznie wskazania do-poprawy             najslabsze
 *   wylacznie wskazania mocna-strona           najlepsze
 *   oba rodzaje                                srednie
 *   zadnego zweryfikowanego wskazania          brak
 *
 * Funkcja jest czysta i nie zna DOM-u: interfejs tlumaczy wynik na klase CSS,
 * eksport na kolor komorki.
 */

export type KolorWypowiedzi = "najslabsze" | "srednie" | "najlepsze" | "brak";

/** Najmniejsze, czego ta funkcja potrzebuje od wskazania. */
export interface WskazanieDoKoloru {
  kind: FindingKind;
  quoteVerified: boolean;
}

/** Kolor jednej wypowiedzi na podstawie jej wskazan (niezweryfikowane sa pomijane). */
export function kolorWypowiedzi(wskazania: readonly WskazanieDoKoloru[]): KolorWypowiedzi {
  let doPoprawy = false;
  let mocneStrony = false;

  for (const w of wskazania) {
    if (!w.quoteVerified) continue;
    if (w.kind === "do-poprawy") doPoprawy = true;
    else if (w.kind === "mocna-strona") mocneStrony = true;
  }

  if (doPoprawy && mocneStrony) return "srednie";
  if (doPoprawy) return "najslabsze";
  if (mocneStrony) return "najlepsze";
  return "brak";
}

/** Wskazanie z numerem wypowiedzi, tak jak API zwraca je klientowi (utteranceIndex od 0). */
export interface WskazanieZWypowiedzia extends WskazanieDoKoloru {
  utteranceIndex: number | null;
}

/**
 * Kolory wszystkich wypowiedzi naraz: mapa indeks wypowiedzi (od 0, jak w bazie)
 * -> kolor. Wypowiedzi bez zadnego wskazania nie maja wpisu (== "brak").
 * Wskazania bez wypowiedzi (utteranceIndex === null) sa pomijane.
 */
export function koloryWypowiedzi(
  wskazania: readonly WskazanieZWypowiedzia[],
): Map<number, Exclude<KolorWypowiedzi, "brak">> {
  const wgWypowiedzi = new Map<number, WskazanieDoKoloru[]>();
  for (const w of wskazania) {
    if (w.utteranceIndex === null) continue;
    const lista = wgWypowiedzi.get(w.utteranceIndex);
    if (lista) lista.push(w);
    else wgWypowiedzi.set(w.utteranceIndex, [w]);
  }

  const wynik = new Map<number, Exclude<KolorWypowiedzi, "brak">>();
  for (const [index, lista] of wgWypowiedzi) {
    const kolor = kolorWypowiedzi(lista);
    if (kolor !== "brak") wynik.set(index, kolor);
  }
  return wynik;
}
